import { memo } from "react";

import { FlexRow, PixelPlaceholderLoader, Tooltip } from "@similarweb/ui-components";

import { ReadOnlyInput } from "app/components/React/read-only-input/ReadOnlyInput";
import { InfoTooltip } from "app/components/React/Tooltip/InfoTooltip/InfoTooltip";
import { translate } from "app/filters/i18nFilter";
import { SetDefaultHomePageButton } from "app/pages/activation/components/Settings/SetDefaultHomePageButton/SetDefaultHomePageButton";

import { CountryDropdown } from "../../Shared/CountryDropdown/CountryDropdown";

import { FILTER_HEIGHT, FILTER_WIDTH, MARGIN_BOTTOM, POP_UP_HEIGHT, SINGLE_COUNTRY } from "./constants";
import { generalSettingsKeys } from "./dataAutomation";
import * as SC from "./styles";
import { DISABLED_MAIN_DOMAIN_INPUT, FILTER_TITLE, PROJECT_NAME, SECOND_FILTER_TOOLTIP, TITLE } from "./texts";
import type { GeneralSettingsSchema } from "./types";

export const GeneralSettings = memo(
    ({
        onChange,
        onNameChange,
        isLoading,
        selectedWebsite,
        countriesList = [],
        selectedCountry,
        activationSetName,
        nameInputError,
        isViewerMode,
        isMultiProjects = false,
    }: GeneralSettingsSchema) => (
        <SC.GeneralSettingsContainer data-automation={generalSettingsKeys.container}>
            <div>
                <FlexRow>
                    <SC.SectionTitle
                        data-automation={generalSettingsKeys.title}
                        marginBottom={MARGIN_BOTTOM}
                    >
                        {translate(PROJECT_NAME)}
                    </SC.SectionTitle>
                </FlexRow>

                <SC.NameInput
                    onChange={onNameChange}
                    value={activationSetName}
                    isError={nameInputError.isError}
                    errorMessage={nameInputError.errorMessage}
                    isLoading={isLoading}
                    isDisabled={isViewerMode}
                />
            </div>

            <SC.WebsiteWrapper data-automation={generalSettingsKeys.website}>
                <FlexRow>
                    <SC.SectionTitle
                        data-automation={generalSettingsKeys.generalTitle}
                        marginBottom={MARGIN_BOTTOM}
                    >
                        {translate(TITLE)}
                    </SC.SectionTitle>
                </FlexRow>

                {isLoading ? (
                    <span data-automation={generalSettingsKeys.preLoader}>
                        <PixelPlaceholderLoader
                            height={FILTER_HEIGHT}
                            width={FILTER_WIDTH}
                        />
                    </span>
                ) : (
                    <Tooltip content={translate(DISABLED_MAIN_DOMAIN_INPUT)}>
                        <span>
                            <ReadOnlyInput
                                displayText={selectedWebsite?.name}
                                image={selectedWebsite?.image}
                                height={`${FILTER_HEIGHT}px`}
                                disabled
                            />
                        </span>
                    </Tooltip>
                )}
            </SC.WebsiteWrapper>

            <FlexRow>
                <SC.SectionTitle
                    data-automation={generalSettingsKeys.filter}
                    marginBottom={MARGIN_BOTTOM}
                >
                    {translate(FILTER_TITLE)}
                </SC.SectionTitle>

                {!isViewerMode ? (
                    <InfoTooltip
                        infoText={translate(SECOND_FILTER_TOOLTIP)}
                        infoIconContainer={SC.InfoIconContainer}
                    />
                ) : null}
            </FlexRow>

            <SC.CountryFilterWrapper
                data-automation={generalSettingsKeys.countryFilter}
                className="settings-country-filter"
                isMultiActivation={isMultiProjects}
            >
                <CountryDropdown
                    appendTo=".settings-country-filter"
                    onClick={onChange}
                    countries={countriesList}
                    selectedCountryId={selectedCountry}
                    buttonWidth={FILTER_WIDTH}
                    dropdownPopupHeight={POP_UP_HEIGHT}
                    dropdownPopupMinScrollHeight={POP_UP_HEIGHT}
                    scrollAreaMinHeight={POP_UP_HEIGHT}
                    isDisabled={countriesList.length <= SINGLE_COUNTRY || isViewerMode}
                    hasSearch
                />
            </SC.CountryFilterWrapper>

            {isMultiProjects ? <SetDefaultHomePageButton activationName={activationSetName} /> : null}
        </SC.GeneralSettingsContainer>
    ),
);

GeneralSettings.displayName = "GeneralSettings";
